import React, { useState, useEffect } from 'react';
import { Play, ThumbsUp, MessageSquare, Eye } from 'lucide-react';
import { supabase } from '../../../../../supabaseClient';

interface MiniCardProps {
  video: any;
  onSelect: (video: any) => void;
}

// Função para calcular o "tempo atrás" (estilo YouTube)
const formatTimeAgo = (dateString?: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'há poucos segundos';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `há ${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''}`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `há ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `há ${diffInMonths} mês${diffInMonths > 1 ? 'es' : ''}`;
  const diffInYears = Math.floor(diffInDays / 365);
  return `há ${diffInYears} ano${diffInYears > 1 ? 's' : ''}`;
};

export default function PlayFeedMiniCard({ video, onSelect }: MiniCardProps) {
  // 1. TODOS OS HOOKS PRIMEIRO (Obrigatório no React)
  const [isHovered, setIsHovered] = useState(false);
  const [likesCount, setLikesCount] = useState<number>(video?.likes_count || 0);
  const [commentsCount, setCommentsCount] = useState<number>(video?.comments_count || 0);
  const [viewsCount, setViewsCount] = useState<number>(video?.views_count || 0);

  // Usamos o '?' (optional chaining) para evitar erros caso o vídeo venha vazio
  const videoId = video?.id || video?._id;
  const cardColor = video?.color || '#E50914';

  // 2. EFEITOS
  useEffect(() => {
    if (!videoId) return;

    setLikesCount(video?.likes_count || 0);
    setCommentsCount(video?.comments_count || 0);
    setViewsCount(video?.views_count || 0);

    const channel = supabase
      .channel(`public:videos:mini:${videoId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'videos',
          filter: `id=eq.${videoId}`,
        },
        (payload) => {
          if (payload.new) {
            setLikesCount(payload.new.likes_count || 0);
            setCommentsCount(payload.new.comments_count || 0);
            setViewsCount(payload.new.views_count || 0);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [videoId, video]);

  // 3. PROTEÇÃO ANTI-TELA BRANCA (Sempre depois dos hooks!)
  if (!video || !videoId) return null;

  // 4. RENDERIZAÇÃO VISUAL
  return (
    <div
      onClick={() => onSelect(video)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        gap: '12px',
        backgroundColor: isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
        borderRadius: '10px',
        cursor: 'pointer',
        padding: '8px',
        transition: 'background-color 0.2s',
      }}
    >
      {/* Capa do Vídeo em miniatura (Thumb) aumentada para 170px */}
      <div style={{
        position: 'relative',
        width: '180px',
        flexShrink: 0,
        aspectRatio: '16/9',
        backgroundColor: '#1c1c1c',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.05)'
      }}>
        <img 
          src={video.thumbnail || video.thumb || video.thumbnail_url || 'https://picsum.photos/seed/error/640/360'} 
          alt={video.title || 'Vídeo'} 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
        />
        
        {/* Ícone de Play e overlay escuro no Hover */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundColor: isHovered ? 'rgba(0,0,0,0.5)' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background-color 0.2s'
        }}>
          {isHovered && (
            <Play size={24} color="#fff" fill="#fff" style={{ marginLeft: '2px', filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.8))' }} />
          )}
        </div>
      </div>

      {/* Informações do Mini Card */}
      {/* Adicionámos minWidth: 0 aqui para evitar que o flexbox esmague o texto */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: '6px', overflow: 'hidden', justifyContent: 'center' }}>
        
        <h4 style={{ 
          color: isHovered ? cardColor : '#fff', 
          margin: 0, 
          fontSize: '13.5px', // Tamanho ligeiramente reduzido para caber melhor
          fontWeight: '500', // Um bocadinho menos grosso para leitura mais leve
          lineHeight: '1.3',
          display: '-webkit-box', 
          WebkitLineClamp: 2, // Garante que fica em 2 linhas
          WebkitBoxOrient: 'vertical', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          wordBreak: 'break-word', // 👈 O truque mágico: quebra o texto com elegância
          paddingRight: '5px', // Dá um respiro do lado direito
          transition: 'color 0.2s'
        }}>
          {video.title || 'Sem título'}
        </h4>

        {/* Categoria e Data misturados */}
        <span style={{ color: '#aaa', fontSize: '12px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {video.category || 'Recomendado'} • {formatTimeAgo(video.created_at)}
        </span>

        {/* Linha dos Ícones um pouco maiores */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#888', fontSize: '12px' }}>
            <Eye size={14} /> {viewsCount}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#888', fontSize: '12px' }}>
            <ThumbsUp size={14} /> {likesCount}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#888', fontSize: '12px' }}>
            <MessageSquare size={14} /> {commentsCount}
          </div>
        </div>

      </div>
    </div>
  );
}